/**
 * Recently Viewed Products
 * Tracks product views using localStorage and displays them dynamically
 */

class RecentlyViewed {
  constructor() {
    this.storageKey = 'newv-recently-viewed';
    this.maxItems = 10;
    this.currentProductHandle = null;
  }

  /**
   * Set current product handle to exclude from recently viewed
   * @param {string} handle - Current product handle
   */
  setCurrentProduct(handle) {
    this.currentProductHandle = handle;
  }

  /**
   * Add a product to recently viewed list
   * @param {Object} product - Product data containing handle and id
   */
  addProduct(product) {
    if (!product || !product.handle) {
      console.warn('RecentlyViewed: Invalid product data');
      return;
    }

    // Don't add current product to recently viewed
    if (this.currentProductHandle && product.handle === this.currentProductHandle) {
      return;
    }

    try {
      const viewedProducts = this.getProducts();

      // Remove if already exists (to move it to front)
      const filteredProducts = viewedProducts.filter(p => p.handle !== product.handle);

      // Add to beginning of array
      filteredProducts.unshift({
        handle: product.handle,
        id: product.id,
        timestamp: Date.now()
      });

      // Limit to max items
      const limitedProducts = filteredProducts.slice(0, this.maxItems);

      // Save to localStorage
      localStorage.setItem(this.storageKey, JSON.stringify(limitedProducts));
    } catch (error) {
      // Handle quota exceeded error
      if (error.name === 'QuotaExceededError') {
        console.error('RecentlyViewed: localStorage quota exceeded');
        // Try to clear old data and retry
        try {
          const viewedProducts = this.getProducts();
          const reducedProducts = viewedProducts.slice(0, 5); // Keep only 5 items
          localStorage.setItem(this.storageKey, JSON.stringify(reducedProducts));
        } catch (retryError) {
          console.error('RecentlyViewed: Failed to save even after cleanup', retryError);
        }
      } else {
        console.error('RecentlyViewed: Error adding product', error);
      }
    }
  }

  /**
   * Get all recently viewed products
   * @returns {Array} Array of product objects
   */
  getProducts() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('RecentlyViewed: Error reading from localStorage', error);
      return [];
    }
  }

  /**
   * Get product handles (for API requests), excluding current product
   * @param {number} limit - Maximum number of handles to return
   * @returns {Array} Array of product handles
   */
  getProductHandles(limit = this.maxItems) {
    const products = this.getProducts();
    // Filter out current product if set
    const filtered = this.currentProductHandle
      ? products.filter(p => p.handle !== this.currentProductHandle)
      : products;

    return filtered.slice(0, limit).map(p => p.handle);
  }

  /**
   * Clear all recently viewed products
   */
  clear() {
    try {
      localStorage.removeItem(this.storageKey);
    } catch (error) {
      console.error('RecentlyViewed: Error clearing storage', error);
    }
  }

  /**
   * Fetch product data from Shopify API
   * @param {string} handle - Product handle
   * @returns {Promise} Product data from Shopify API
   */
  async fetchProductData(handle) {
    // Check if Shopify API is available
    if (!window.Shopify || !window.Shopify.routes) {
      console.error('RecentlyViewed: Shopify API not available');
      return null;
    }

    try {
      const response = await fetch(`${window.Shopify.routes.root}products/${handle}.js`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`RecentlyViewed: Error fetching product ${handle}`, error);
      return null;
    }
  }

  /**
   * Fetch multiple products data with batching
   * @param {Array} handles - Array of product handles
   * @returns {Promise<Array>} Array of product data
   */
  async fetchProducts(handles) {
    // Batch requests to avoid overwhelming the browser
    const batchSize = 3;
    const results = [];

    for (let i = 0; i < handles.length; i += batchSize) {
      const batch = handles.slice(i, i + batchSize);
      const promises = batch.map(handle => this.fetchProductData(handle));
      const batchResults = await Promise.all(promises);
      results.push(...batchResults);
    }

    // Filter out any failed requests
    return results.filter(product => product !== null);
  }
}

// Initialize as singleton
window.RecentlyViewed = new RecentlyViewed();

// Track product view on product pages - wait for DOM to be ready
function initProductTracking() {
  const productInfo = document.querySelector('product-info[data-product-id]');

  if (!productInfo) {
    return;
  }

  const productId = productInfo.getAttribute('data-product-id');
  const productUrl = productInfo.getAttribute('data-url');

  if (!productId || !productUrl) {
    return;
  }

  // Extract handle from URL
  const urlParts = productUrl.split('/');
  const handle = urlParts[urlParts.length - 1].split('?')[0];

  // Set current product to exclude from recently viewed display
  window.RecentlyViewed.setCurrentProduct(handle);

  let hasTracked = false;

  // Add to recently viewed when leaving the page
  const trackOnUnload = () => {
    if (!hasTracked) {
      window.RecentlyViewed.addProduct({
        id: productId,
        handle: handle
      });
      hasTracked = true;
    }
  };

  window.addEventListener('beforeunload', trackOnUnload);

  // Also track after 5 seconds (for users who stay on page)
  // This covers the case where users don't navigate away
  setTimeout(() => {
    if (!hasTracked) {
      window.RecentlyViewed.addProduct({
        id: productId,
        handle: handle
      });
      hasTracked = true;
      // Remove beforeunload listener since we've already tracked
      window.removeEventListener('beforeunload', trackOnUnload);
    }
  }, 5000);
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductTracking);
} else {
  initProductTracking();
}
